import { useCallback, useState } from "react";
import { redoCategorization, undoCategorization } from "../api/transactions";
import type { Categorization } from "../types/transaction";

type Sent = Record<number, number | null>;

export interface RowSteps {
  undo: (itemId: number) => Promise<void>;
  redo: (itemId: number) => Promise<void>;
  /** True while the row's step is in flight or its refreshed item is due (2.8 (3)). */
  isUndoDisabled: (item: Categorization) => boolean;
  isRedoDisabled: (item: Categorization) => boolean;
}

function isSentFor(sent: Sent, item: Categorization): boolean {
  const logId = sent[item.id];
  return logId !== undefined && logId === item.last_log_id;
}

function dropKey(sent: Sent, id: number): Sent {
  const next = { ...sent };
  delete next[id];
  return next;
}

type Step = typeof undoCategorization | typeof redoCategorization;

/**
 * Undo and Redo of one row, each sending the row's `last_log_id` as
 * `expected_last_log_id`. A button stays disabled from the press until the
 * refreshed item carries a newer log line, so two presses send one request.
 */
export function useRowSteps(
  clientId: number,
  items: Categorization[],
  onUpdated: () => void,
  onRefused: (err: unknown) => void,
): RowSteps {
  const [undoSent, setUndoSent] = useState<Sent>({});
  const [redoSent, setRedoSent] = useState<Sent>({});

  const step = useCallback(
    async (itemId: number, send: Step, setSent: (update: (prev: Sent) => Sent) => void) => {
      const item = items.find((i) => i.id === itemId);
      if (!item) return;
      setSent((prev) => ({ ...prev, [itemId]: item.last_log_id }));
      try {
        await send(clientId, itemId, item.last_log_id);
        onUpdated();
      } catch (err) {
        setSent((prev) => dropKey(prev, itemId));
        onRefused(err);
      }
    },
    [clientId, items, onUpdated, onRefused],
  );

  const undo = useCallback((id: number) => step(id, undoCategorization, setUndoSent), [step]);
  const redo = useCallback((id: number) => step(id, redoCategorization, setRedoSent), [step]);
  return {
    undo,
    redo,
    isUndoDisabled: (item) => isSentFor(undoSent, item),
    isRedoDisabled: (item) => isSentFor(redoSent, item),
  };
}
